const { getValidatedIframeData, isObjectEmpty } = require('../util');
const { method } = require('../variables');
const {
  CSS_CLASSES,
  EVENT_TYPES,
  HEIGHT_ADJUSTMENTS,
  HTTP_STATUS,
  HTTP_STATUS_RANGE,
  IFRAME_TYPES,
  PAYMENT_OUTCOME,
  SCRIPT_PROPS,
  STATUS,
  PAYMENT_TYPES,
} = require('../constants');
const { captureError } = require('../sentry');
const { mainConfigService } = require('../services/config');
const {
  extractCardDataFromEvent,
  extractCardDataFromMethod,
} = require('../utils/cardDataExtractor');

class JazzIntegration {
  constructor({
    selector,
    fieldsService,
    callBackService,
    sessionId,
    merchantId,
    apiKey,
    data,
    paymentType,
    lang,
  }) {
    this.selector = selector;
    this.fieldsService = fieldsService;
    this.callBackService = callBackService;
    this.documentCheckout = selector.documentCheckout;
    this.sessionId = sessionId;
    this.merchantId = merchantId;
    this.apiKey = apiKey;
    this.data = data;
    this.paymentType = paymentType;
    this.lang = lang;

    this.SDKEventBus = new EventTarget();
    this.jazzSdk = null;
    this.iframeManagers = new Map(); // Store iframe managers by their IDs
    this.isInitialized = false;
    // Iframes whose 401/403 has already been reported, so a persistent
    // credentials problem raises one Sentry event rather than one per retry.
    this.reportedNotAuthorizedIframes = new Set();
  }

  /**
   * Picks the Jazz SDK build matching the environment behind the session.
   * The URL and its SRI hash are chosen together so they can never be mixed,
   * and an unconfigured sandbox URL falls back to production rather than
   * producing a script tag with an empty src.
   */
  getSDKScriptSource() {
    const useSandbox =
      mainConfigService.get('is_sandbox') === true &&
      !!process.env.JAZZ_SDK_SANDBOX_URL;

    if (useSandbox) {
      return {
        url: process.env.JAZZ_SDK_SANDBOX_URL,
        integrityHash: process.env.JAZZ_SDK_SANDBOX_INTEGRITY_HASH,
      };
    }

    return {
      url: process.env.JAZZ_SDK_URL,
      integrityHash: process.env.JAZZ_SDK_INTEGRITY_HASH,
    };
  }

  async loadSDKScript(containerElement) {
    return new Promise((resolve, reject) => {
      const { url, integrityHash } = this.getSDKScriptSource();
      const script = document.createElement('script');
      script.src = url;
      script.type = SCRIPT_PROPS.TYPE_JAVASCRIPT;

      // Add SRI (Subresource Integrity) attributes if hash is provided and not running locally
      if (integrityHash && process.env.IS_LOCAL !== 'true') {
        script.integrity = integrityHash;
        script.crossOrigin = 'anonymous';
      }

      script.onload = () => {
        console.log('[JazzSDK] Jazz SDK loaded successfully');
        resolve();
      };
      script.onerror = () => {
        const errorMessage = integrityHash
          ? 'Failed to load JazzSDK script. This could be due to a security integrity check failure or network issue.'
          : 'Failed to load JazzSDK script';
        console.error('[JazzSDK] Failed to load Jazz SDK:', errorMessage);
        reject(new Error(errorMessage));
      };
      containerElement.appendChild(script);
    });
  }

  async init() {
    if (this.isInitialized) {
      return; // Already initialized
    }

    // Load SDK script into the document head instead of a specific container
    await this.loadSDKScript(document.head);

    // Initialize JazzSDK once
    this.jazzSdk = new window.JazzSDK({
      apiKey: this.apiKey,
      merchantId: this.merchantId,
      eventTarget: this.SDKEventBus,
      lang: this.lang,
    });

    this.setupEventListeners();
    this.setupIframeReadyListener();
    this.isInitialized = true;
  }

  /**
   * Returns the merchant theme to forward to the Jazz iframe, or a fallback
   * that makes the iframe's card-form border transparent so it doesn't draw
   * a second border on top of the SDK-level card outline.
   */
  getTheme() {
    const theme = mainConfigService.get('theme');
    if (theme && !isObjectEmpty(theme)) {
      return theme;
    }
    return {
      'card-background': {
        'border-color': 'transparent',
      },
    };
  }

  /**
   * Creates a card iframe with the given configuration
   * @param {Object} config - Configuration for the card iframe
   * @param {string} config.containerId - ID of the container element
   * @param {string} config.iframeName - Name for the iframe
   * @param {Object} config.postData - Data to post to the iframe
   * @returns {Object} - Object containing iframeId and iframeManager
   */
  createCardIframe({ containerId, iframeName, postData }) {
    if (!this.isInitialized) {
      throw new Error(
        'JazzIntegration must be initialized before creating iframes'
      );
    }
    const container = this.documentCheckout.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with ID "${containerId}" not found`);
    }

    const validatedData = getValidatedIframeData(postData || this.data);
    const directPayConfig = {
      ...validatedData,
      theme: this.getTheme(),
      session_id: this.sessionId,
      payment_type: this.paymentType,
    };

    const iframeConfig = {
      container,
      iframeName,
      postData: directPayConfig,
    };

    const result = this.jazzSdk.createCardIframe(iframeConfig);
    this.iframeManagers.set(result.iframeId, {
      type: IFRAME_TYPES.CARD,
      containerId,
      iframeName,
      manager: result.iframeManager,
    });

    const iframe = this.documentCheckout.getElementById(result.iframeId);
    if (iframe) {
      iframe.classList.add(CSS_CLASSES.DISPLAY_NONE);
    }

    return result;
  }

  /**
   * Creates a CVV iframe with the given configuration
   * @param {Object} config - Configuration for the CVV iframe
   * @param {string} config.containerId - ID of the container element
   * @param {string} config.iframeName - Name for the iframe
   * @param {Object} config.postData - Data to post to the iframe
   * @returns {Object} - Object containing iframeId and iframeManager
   */
  createCvvIframe({ containerId, iframeName, postData }) {
    if (!this.isInitialized) {
      throw new Error(
        'JazzIntegration must be initialized before creating iframes'
      );
    }

    const container = this.documentCheckout.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with ID "${containerId}" not found`);
    }

    const matchedMethod = mainConfigService
      .get('payment_methods')
      ?.find((pm) => pm.code === postData.pg_code);

    const validatedData = getValidatedIframeData({
      ...matchedMethod,
      ...postData,
    });

    const directPayConfig = {
      ...validatedData,
      theme: this.getTheme(),
      session_id: this.sessionId,
      payment_type: this.paymentType,
    };

    const iframeConfig = {
      container,
      iframeName,
      postData: directPayConfig,
    };

    const result = this.jazzSdk.createCvvIframe(iframeConfig);
    this.iframeManagers.set(result.iframeId, {
      type: IFRAME_TYPES.CVV,
      containerId,
      iframeName,
      manager: result.iframeManager,
    });

    const iframe = this.documentCheckout.getElementById(result.iframeId);
    if (iframe) {
      iframe.classList.add(CSS_CLASSES.DISPLAY_NONE);
    }

    return result;
  }

  setupIframeReadyListener() {
    const self = this;
    window.addEventListener('message', (event) => {
      const { type, iframeId, containerId } = event.data || {};
      // Check if we have this iframe registered
      if (!self.iframeManagers.has(iframeId)) {
        return;
      }

      if (
        type === EVENT_TYPES.CVV_IFRAME_READY ||
        type === EVENT_TYPES.IFRAME_READY
      ) {
        const loader = self.documentCheckout.getElementById(
          `loader-${containerId}`
        );
        const iframe = self.documentCheckout.getElementById(iframeId);

        if (iframe) {
          // Handle CVV iframe separately from Card iframe
          if (type === EVENT_TYPES.CVV_IFRAME_READY) {
            iframe.classList.add(CSS_CLASSES.CVV_STYLES);

            // CVV iframe fade-in with saved-card-loader fade-out
            const cardLoaders =
              self.documentCheckout.querySelectorAll('.saved-card-loader');

            cardLoaders.forEach((cardLoader) => {
              const cardKey = cardLoader.getAttribute('data-card-key');
              if (containerId.includes(cardKey)) {
                // Prepare CVV iframe for fade-in
                iframe.classList.remove(CSS_CLASSES.DISPLAY_NONE);
                iframe.classList.add(CSS_CLASSES.IFRAME_FADE);

                // Force reflow
                iframe.offsetHeight;

                // Start cross-fade animation
                requestAnimationFrame(() => {
                  // Fade in CVV iframe
                  iframe.classList.add(CSS_CLASSES.IFRAME_VISIBLE);

                  // Fade out saved-card-loader simultaneously
                  cardLoader.classList.add(
                    CSS_CLASSES.SAVED_CARD_LOADER_FADE_OUT
                  );

                  // Remove loader from DOM after transition completes
                  setTimeout(() => {
                    cardLoader.classList.add(
                      CSS_CLASSES.SAVED_CARD_LOADER_LOADED
                    );
                  }, 300);
                });
              }
            });
          } else if (type === EVENT_TYPES.IFRAME_READY) {
            // Handle Card iframe with loader cross-fade
            // Step 1: Set up parent container with proper positioning and height
            const parentContainer = iframe.parentElement;
            if (parentContainer && loader) {
              const loaderHeight = loader.offsetHeight;
              // Reserve space in layout by setting height on parent
              parentContainer.style.height = `${loaderHeight}px`;
              parentContainer.classList.add(
                CSS_CLASSES.IFRAME_TRANSITION_CONTAINER
              );
            }

            // Step 2: Position iframe absolutely (like the loader) to prevent layout shift
            iframe.classList.add(CSS_CLASSES.IFRAME_TRANSITION_ABSOLUTE);

            // Step 3: Prepare iframe for fade-in (but keep it invisible)
            iframe.classList.remove(CSS_CLASSES.DISPLAY_NONE);
            iframe.classList.add(CSS_CLASSES.IFRAME_FADE);

            // Force reflow to ensure initial state is applied
            iframe.offsetHeight;

            // Step 4: Start cross-fade animation
            requestAnimationFrame(() => {
              // Fade in iframe
              iframe.classList.add(CSS_CLASSES.IFRAME_VISIBLE);

              // Fade out loader simultaneously
              if (loader) {
                loader.classList.add(CSS_CLASSES.LOADER_FADE_OUT);
              }

              // Step 5: Clean up after transition completes
              setTimeout(() => {
                if (loader) {
                  loader.classList.add(CSS_CLASSES.DISPLAY_NONE);
                }

                // Return iframe to normal flow and let it size naturally
                iframe.classList.remove(CSS_CLASSES.IFRAME_TRANSITION_ABSOLUTE);

                // Let parent container size naturally
                if (parentContainer) {
                  parentContainer.style.height = '';
                  parentContainer.classList.remove(
                    CSS_CLASSES.IFRAME_TRANSITION_CONTAINER
                  );
                }
              }, 300); // Match the transition duration
            });
          }
        }
      }
    });
  }

  setupEventListeners() {
    this._setupCardValidationListener();
    this._setupCvvValidationListener();
    this._setupHandlePaymentProcessListener();
    this._setupCardEncryptionTriggerListener();
    this._setupEncryptionFailureListener();
    this._setupOnsiteFormHeightChangeListener();
  }

  _setupCardValidationListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.VALID_CARD_FIELD,
      async (event) => {
        try {
          const iframeId = event.detail?.iframeId;

          // Check if we have this iframe registered
          if (!this.iframeManagers.has(iframeId)) {
            return;
          }

          this.fieldsService.disableEnableFields(true);

          // Extract card data and invoke callback
          const card = extractCardDataFromEvent(event.detail);
          await this._invokeBeforePaymentCallback(card);

          const paymentType = mainConfigService.get('payment_type');
          const paymentMethod = mainConfigService
            .get('payment_methods')
            ?.find((m) => m.code === method.code);

          if (paymentType === PAYMENT_TYPES.SAVE_CARD) {
            method.save_card = true;
            method.direct_payment_url =
              paymentMethod.direct_save_card_url || method.direct_payment_url;
          } else {
            const saveCardCheckbox = this.documentCheckout.querySelector(
              '#ottu__sdk-card-save'
            );
            method.save_card = saveCardCheckbox
              ? saveCardCheckbox.checked
              : false;
          }

          this.jazzSdk.submitCardDetails(iframeId, method);
        } catch (err) {
          this._handleJazzEventError(err);
        }
      }
    );
  }

  _setupCvvValidationListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.VALID_CVV_FIELD,
      async (event) => {
        try {
          const iframeId = event.detail?.iframeId;

          // Check if we have this iframe registered
          if (!this.iframeManagers.has(iframeId)) {
            return;
          }

          const configData = {
            merchant_id: this.merchantId,
            api_key: this.apiKey,
          };
          const matchedMethod = mainConfigService
            .get('payment_methods')
            ?.find((pm) => pm.code === method.card_pg);
          configData.encKeyUrl = matchedMethod.enc_key_url;
          configData.trandata = matchedMethod.trandata;
          configData.submit_url = method.submit_url;

          this.fieldsService.disableEnableFields(true);

          // Extract card data from method and invoke callback
          const card = extractCardDataFromMethod(method);
          await this._invokeBeforePaymentCallback(card);

          this.jazzSdk.submitCvv(iframeId, method, configData);
        } catch (err) {
          this._handleJazzEventError(err);
        }
      }
    );
  }

  _setupHandlePaymentProcessListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.HANDLE_PAYMENT_PROCESS,
      (event) => {
        const data = event.detail;
        const iframeId = data?.iframeId;
        // Check if we have this iframe registered
        if (!this.iframeManagers.has(iframeId)) {
          return;
        }

        this.fieldsService.disableEnableFields(false);

        const { kind, httpStatus, textStatus } =
          this._classifyPaymentOutcome(data);

        switch (kind) {
        case PAYMENT_OUTCOME.SUCCESS:
          this._handlePaymentSuccess(iframeId, data, textStatus);
          return;

        case PAYMENT_OUTCOME.API_ERROR:
          // An explicit error status keeps its dedicated 3DS-aware path.
          this._enablePayButton();
          this.selector.errorResponse(data);
          this.jazzSdk.handle3DSApiError(iframeId);
          this.jazzSdk.handleFieldErrors(iframeId, {});
          return;

        case PAYMENT_OUTCOME.FIELD_ERROR:
          this._enablePayButton();
          this.jazzSdk.handleFieldErrors(iframeId, data);
          return;

        case PAYMENT_OUTCOME.SERVER_ERROR:
          this._enablePayButton();
          this.callBackService.callBackError(data);
          this.jazzSdk.handleFieldErrors(iframeId, {});
          return;

        case PAYMENT_OUTCOME.NOT_AUTHORIZED:
          // Expired or unauthorised credentials are a merchant configuration
          // problem, so surface them rather than let the shopper retry into
          // the same rejection silently. Reported after the payment has been
          // routed, so diagnostics can never interfere with the flow.
          this._handlePaymentFailure(iframeId, data);
          this._reportNotAuthorized(iframeId, data, httpStatus);
          return;

        case PAYMENT_OUTCOME.CLIENT_ERROR:
          this._handlePaymentFailure(iframeId, data);
          return;

        case PAYMENT_OUTCOME.TRANSPORT_ERROR:
          // A request the Jazz SDK could not complete at all (network
          // failure, unreadable response): a known failure with no status.
          // `data.error` is a real Error, so errorResponse() reports its
          // message to the merchant instead of "status undefined".
          this._enablePayButton();
          this.selector.errorResponse(data.error);
          this.jazzSdk.handleFieldErrors(iframeId, {});
          return;

        default:
          this._handleUnmappedPaymentStatus(iframeId, data, httpStatus);
          return;
        }
      }
    );
  }

  /**
   * Sorts a payment-process event into one outcome class. Pure: it performs no
   * UI, callback or reporting side effects, so the classification rules can be
   * asserted directly.
   *
   * Precedence note: a textual `error` status is classified before the HTTP
   * status, so a 400 whose body also reports `status: 'error'` takes the
   * 3DS-aware path rather than the field-error path. That ordering is
   * deliberate and pre-dates this classifier — the 3DS teardown matters more
   * than per-field messages on a payment that already failed at the gateway.
   * @private
   * @param {Object} data - Raw payment-process event payload
   * @returns {{kind: string, httpStatus: (number|null), textStatus: (string|null)}}
   */
  _classifyPaymentOutcome(data) {
    const textStatus =
      typeof data?.status === 'string' ? data.status.toLowerCase() : null;
    const httpStatus = this._resolveHttpStatus(data);

    if (textStatus === STATUS.ERROR) {
      return { kind: PAYMENT_OUTCOME.API_ERROR, httpStatus, textStatus };
    }

    // No HTTP status on the event: fall back to the payment results the Jazz
    // SDK reports as plain text.
    if (httpStatus === null) {
      if (
        textStatus === STATUS.SUCCESS ||
        textStatus === STATUS.CANCELED ||
        textStatus === STATUS.FAILED
      ) {
        return { kind: PAYMENT_OUTCOME.SUCCESS, httpStatus, textStatus };
      }
      if (data?.error) {
        return { kind: PAYMENT_OUTCOME.TRANSPORT_ERROR, httpStatus, textStatus };
      }
      return { kind: PAYMENT_OUTCOME.UNMAPPED, httpStatus, textStatus };
    }

    // Success is an explicit 2xx check, never "not a known error".
    if (
      httpStatus >= HTTP_STATUS_RANGE.SUCCESS.MIN &&
      httpStatus <= HTTP_STATUS_RANGE.SUCCESS.MAX
    ) {
      return { kind: PAYMENT_OUTCOME.SUCCESS, httpStatus, textStatus };
    }

    if (httpStatus === HTTP_STATUS.BAD_REQUEST) {
      return { kind: PAYMENT_OUTCOME.FIELD_ERROR, httpStatus, textStatus };
    }

    if (
      httpStatus >= HTTP_STATUS_RANGE.SERVER_ERROR.MIN &&
      httpStatus <= HTTP_STATUS_RANGE.SERVER_ERROR.MAX
    ) {
      return { kind: PAYMENT_OUTCOME.SERVER_ERROR, httpStatus, textStatus };
    }

    if (
      httpStatus >= HTTP_STATUS_RANGE.CLIENT_ERROR.MIN &&
      httpStatus <= HTTP_STATUS_RANGE.CLIENT_ERROR.MAX
    ) {
      const kind =
        httpStatus === HTTP_STATUS.UNAUTHORIZED ||
        httpStatus === HTTP_STATUS.FORBIDDEN
          ? PAYMENT_OUTCOME.NOT_AUTHORIZED
          : PAYMENT_OUTCOME.CLIENT_ERROR;
      return { kind, httpStatus, textStatus };
    }

    // 1xx, 3xx or anything else we do not map.
    return { kind: PAYMENT_OUTCOME.UNMAPPED, httpStatus, textStatus };
  }

  /**
   * Routes a successful payment result to the merchant success callback.
   * @private
   * @param {string} iframeId - ID of the iframe the result belongs to
   * @param {Object} data - Raw payment-process event payload
   * @param {string|null} textStatus - Lower-cased textual status, when present
   */
  _handlePaymentSuccess(iframeId, data, textStatus) {
    // A canceled or failed payment still completed its request, but the
    // shopper stays on the form and needs the pay button back.
    if (textStatus === STATUS.CANCELED || textStatus === STATUS.FAILED) {
      this._enablePayButton();
    }
    this.selector.successResponse(data);
    this.jazzSdk.handleFieldErrors(iframeId, {});
  }

  /**
   * Reports a failed payment to both audiences: the merchant receives the
   * gateway payload through the error callback, and the shopper is shown the
   * reason the gateway gave.
   *
   * `errorResponse()` renders `detail`, per-field error arrays and `message`
   * only when handed a thenable — the shape `sdkFetch` produces for a non-2xx
   * body (`http.js` `handleApiResponse`). Jazz delivers the same body as a
   * plain object on an event, so it is wrapped to reach that renderer; handed
   * over unwrapped it would reach the merchant callback only, leaving the
   * shopper with no reason on screen.
   * @private
   * @param {string} iframeId - ID of the iframe the result belongs to
   * @param {Object} data - Raw payment-process event payload
   */
  _handlePaymentFailure(iframeId, data) {
    this._enablePayButton();
    this.callBackService.callBackError(data);
    this.selector.errorResponse(Promise.resolve(data));
    this.jazzSdk.handleFieldErrors(iframeId, {});
  }

  /**
   * Handles an outcome the SDK cannot map to a known status. Treated as a
   * failure and reported, so unknown Jazz outcomes surface instead of being
   * passed to the merchant as successful payments.
   * @private
   * @param {string} iframeId - ID of the iframe the result belongs to
   * @param {Object} data - Raw payment-process event payload
   * @param {number|null} httpStatus - Resolved HTTP status, when present
   */
  _handleUnmappedPaymentStatus(iframeId, data, httpStatus) {
    // Routed before it is reported, so diagnostics can never interfere with
    // the payment flow.
    this._handlePaymentFailure(iframeId, data);
    this._capturePaymentStatus(
      iframeId,
      data,
      httpStatus,
      'jazz_unmapped_payment_status',
      `Unmapped Jazz payment status: ${httpStatus === null ? data?.status : httpStatus}`
    );
  }

  /**
   * Reports a 401/403 once per iframe. An expired or unentitled API key is a
   * persistent merchant misconfiguration, so every shopper retry would
   * otherwise raise another Sentry event for the same root cause.
   * @private
   * @param {string} iframeId - ID of the iframe the result belongs to
   * @param {Object} data - Raw payment-process event payload
   * @param {number} httpStatus - Resolved HTTP status
   */
  _reportNotAuthorized(iframeId, data, httpStatus) {
    if (this.reportedNotAuthorizedIframes.has(iframeId)) {
      return;
    }
    this.reportedNotAuthorizedIframes.add(iframeId);
    this._capturePaymentStatus(
      iframeId,
      data,
      httpStatus,
      'jazz_payment_not_authorized',
      `Jazz payment rejected with HTTP ${httpStatus}`
    );
  }

  /**
   * Reads the HTTP status out of a payment-process event.
   *
   * The Jazz SDK forwards the response status on `httpStatus`, but it throws
   * on a 5xx instead of forwarding it, so a server error only carries its
   * status on `error.statusCode`. `status` is a field of the gateway response
   * body and is read last, for payloads that report the status there.
   * @private
   * @param {Object} data - Raw payment-process event payload
   * @returns {number|null} The HTTP status, or null when the event carries none
   */
  _resolveHttpStatus(data) {
    const candidates = [data?.httpStatus, data?.error?.statusCode, data?.status];
    for (const candidate of candidates) {
      const httpStatus = this._toHttpStatus(candidate);
      if (httpStatus !== null) {
        return httpStatus;
      }
    }
    return null;
  }

  /**
   * Coerces a value that may be a number or a numeric string to an HTTP
   * status code. `status` is a response-body field before it is anything
   * else, so values outside the HTTP range (a body reporting `status: 0` or
   * `status: 1`) are rejected rather than read as a status.
   * @private
   * @param {*} value - Value to coerce
   * @returns {number|null} The status code, or null when the value is not one
   */
  _toHttpStatus(value) {
    const candidate =
      typeof value === 'number'
        ? value
        : typeof value === 'string' && /^\d+$/.test(value.trim())
          ? Number(value.trim())
          : NaN;

    if (
      Number.isInteger(candidate) &&
      candidate >= HTTP_STATUS_RANGE.INFORMATIONAL.MIN &&
      candidate <= HTTP_STATUS_RANGE.SERVER_ERROR.MAX
    ) {
      return candidate;
    }
    return null;
  }

  /**
   * Removes the disabled state from the pay button so the shopper can retry.
   * @private
   */
  _enablePayButton() {
    const payButton = this.selector._checkPayButtonExists();
    payButton?.classList?.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
  }

  /**
   * Reports a payment outcome to Sentry with the iframe id and a curated set
   * of payload fields.
   *
   * Only identified fields are sent: `event.extra` PII scrubbing covers
   * payment secrets, not the customer name, email or phone that gateway
   * response bodies routinely carry.
   * @private
   * @param {string} iframeId - ID of the iframe the result belongs to
   * @param {Object} data - Raw payment-process event payload
   * @param {number|null} httpStatus - Resolved HTTP status, when present
   * @param {string} tag - Value for the `sdk_error` Sentry tag
   * @param {string} message - Message reported to Sentry
   */
  _capturePaymentStatus(iframeId, data, httpStatus, tag, message) {
    captureError(new Error(message), {
      tags: { sdk_error: tag },
      extra: {
        iframe_id: iframeId,
        iframe_type: data?.iframeType,
        http_status: httpStatus,
        raw_status: data?.status,
        raw_http_status: data?.httpStatus,
        error_code: data?.code ?? data?.error_code,
        error_detail: data?.detail,
        error_message: data?.message ?? data?.error?.message,
        order_no: data?.order_no,
        reference_number: data?.reference_number,
        payload_keys: this._describePayloadKeys(data),
      },
    });
  }

  /**
   * Lists the keys a payload carried, so an unmapped outcome can be diagnosed
   * without shipping the values themselves.
   * @private
   * @param {Object} data - Raw payment-process event payload
   * @returns {Array<string>|null} The payload's own keys, when it has any
   */
  _describePayloadKeys(data) {
    return data && typeof data === 'object' ? Object.keys(data) : null;
  }

  _setupOnsiteFormHeightChangeListener() {
    this.SDKEventBus.addEventListener(EVENT_TYPES.CHANGE_HEIGHT, (event) => {
      const data = event.detail;
      const iframeId = data?.iframeId;

      // Check if we have this iframe registered
      if (!this.iframeManagers.has(iframeId)) {
        return;
      }

      const iframeDiv = this.selector._changeOnsiteIframeHeight();
      const newHeight = data.height + HEIGHT_ADJUSTMENTS.ONSITE_FORM_PADDING;
      iframeDiv.style.height = `${newHeight}px`;
    });
  }

  _setupCardEncryptionTriggerListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.CARD_ENCRYPTION_STARTED,
      (event) => {
        const iframeId = event.detail?.iframeId;

        // Check if we have this iframe registered
        if (!this.iframeManagers.has(iframeId)) {
          return;
        }

        const payButton = this.selector._checkPayButtonExists();
        payButton?.classList?.add(CSS_CLASSES.DISABLED_PAY_BUTTON);
      }
    );
  }

  _setupEncryptionFailureListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.ENCRYPTION_FAILURE,
      (event) => {
        const data = event.detail;
        const iframeId = data?.iframeId;

        // Check if we have this iframe registered
        if (!this.iframeManagers.has(iframeId)) {
          return;
        }

        if (data.error) {
          this.selector.errorResponse(data.error);
          this.fieldsService.disableEnableFields(false);
          const payButton = this.selector._checkPayButtonExists();
          payButton?.classList?.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
        }
      }
    );
  }

  /**
   * Invokes beforePayment callback with card data
   * Handles both cases: with card data and without (backward compatible)
   * @private
   * @param {Object} card - Card data object
   * @returns {Promise<void>}
   */
  async _invokeBeforePaymentCallback(card) {
    const hasData = !!(card.number || card.brand);

    if (hasData) {
      await this.callBackService.callBackPayment({ card });
    } else {
      await this.callBackService.callBackPayment();
    }
  }

  /**
   * Handles errors from Jazz SDK event listeners
   * Standardizes error handling across all Jazz integration event handlers
   * @private
   * @param {Error} error - The error that occurred
   */
  _handleJazzEventError(error) {
    this.fieldsService.disableEnableFields(false);
    console.error(error);
    this.selector.errorResponse(error);
  }

  /**
   * Validates card fields for a specific iframe
   * @param {string} iframeId - ID of the iframe to validate
   */
  validateCardFields(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }
    this.jazzSdk?.validateCardFields(iframeId);
  }

  /**
   * Validates CVV field for a specific iframe
   * @param {string} iframeId - ID of the iframe to validate
   */
  validateCvvField(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }
    this.jazzSdk?.validateCvvField(iframeId);
  }

  /**
   * Removes CSS classes for a specific iframe
   * @param {string} iframeId - ID of the iframe
   */
  unselectOnsiteMethod(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }
    this.jazzSdk?.removeCSSClasses(iframeId);
  }

  /**
   * Gets all iframe IDs managed by this instance
   * @returns {Array} - Array of iframe IDs
   */
  getAllIframeIds() {
    return Array.from(this.iframeManagers.keys());
  }

  /**
   * Gets iframe manager information
   * @param {string} iframeId - ID of the iframe
   * @returns {Object} - Iframe manager information
   */
  getIframeManager(iframeId) {
    return this.iframeManagers.get(iframeId);
  }

  /**
   * Destroys a specific iframe
   * @param {string} iframeId - ID of the iframe to destroy
   */
  destroyIframe(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }

    this.jazzSdk?.destroyIframe(iframeId);
    this.iframeManagers.delete(iframeId);
  }

  /**
   * Destroys all iframes and cleans up the instance
   */
  destroy() {
    // Destroy all iframes
    this.getAllIframeIds().forEach((iframeId) => {
      this.jazzSdk?.destroyIframe(iframeId);
    });

    this.iframeManagers.clear();
    this.jazzSdk = null;
    this.isInitialized = false;
  }
}

exports.JazzIntegration = JazzIntegration;
